import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Save,
  Plus,
  Trash2,
  Image,
  Type,
  HelpCircle,
  Eye,
  EyeOff,
  ArrowLeft,
  Upload,
  X,
  Move,
  Copy
} from 'lucide-react'
import { RootState } from '../../store'
import {
  createDeck,
  updateDeck,
  fetchDeckById,
  createCard as createCardThunk,
  updateCard as updateCardThunk,
  deleteCard as deleteCardThunk
} from '../../store/slices/flashcardSlice'
import { unwrapResult } from '@reduxjs/toolkit'
import { Flashcard, FlashcardDeck } from '../../types/flashcard'
import { LGS_SUBJECTS, getTopicsBySubject, DIFFICULTY_LEVELS } from '../../constants/lgsSubjects'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import TextArea from '../../components/ui/TextArea'
import Badge from '../../components/ui/Badge'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'

interface CardFormData {
  id?: string
  question: string
  answer: string
  hint?: string
  explanation?: string
  imageUrl?: string
  tags: string[]
  difficulty: 'easy' | 'medium' | 'hard'
}

interface DeckFormData {
  title: string
  description: string
  subject: string
  topic: string
  difficulty: 'easy' | 'medium' | 'hard'
  isPublic: boolean
  isFavorite: boolean
  tags: string[]
}

const CardEditor: React.FC<{
  card: CardFormData
  onUpdate: (card: CardFormData) => void
  onDelete: () => void
  onDuplicate: () => void
  isPreview?: boolean
}> = ({ card, onUpdate, onDelete, onDuplicate, isPreview = false }) => {
  const [isFlipped, setIsFlipped] = useState(false)
  const [showImageUpload, setShowImageUpload] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  // Debug: showDeleteConfirm state değişikliklerini izle
  useEffect(() => {
    console.log(`=== CARD ${card.id || 'new'} - showDeleteConfirm changed to:`, showDeleteConfirm)
  }, [showDeleteConfirm, card.id])
  
  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !card.tags.includes(tag.trim())) {
      onUpdate({
        ...card,
        tags: [...card.tags, tag.trim()]
      })
    }
  }
  
  const handleTagRemove = (tagToRemove: string) => {
    onUpdate({
      ...card,
      tags: card.tags.filter(tag => tag !== tagToRemove)
    })
  }
  
  if (isPreview) {
    return (
      <Card className="p-6 mb-4">
        <div className="relative">
          <div 
            className={`flashcard-container ${isFlipped ? 'flipped' : ''}`}
            onClick={() => setIsFlipped(!isFlipped)}
          >
            <div className="flashcard-front bg-white border-2 border-gray-200 rounded-lg p-6 min-h-[200px] flex items-center justify-center cursor-pointer">
              <div className="text-center">
                {card.imageUrl && (
                  <img 
                    src={card.imageUrl} 
                    alt="Soru görseli" 
                    className="max-w-full max-h-32 mx-auto mb-4 rounded"
                  />
                )}
                <p className="text-lg font-medium text-gray-900">
                  {card.question}
                </p>
                {card.hint && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      💡 {card.hint}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flashcard-back bg-blue-50 border-2 border-blue-200 rounded-lg p-6 min-h-[200px] flex items-center justify-center cursor-pointer">
              <div className="text-center">
                <p className="text-lg font-medium text-gray-900 mb-4">
                  {card.answer}
                </p>
                {card.explanation && (
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-700">
                      📝 {card.explanation}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="absolute top-2 right-2 flex gap-2">
            <Badge className={`${
              card.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
              card.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {card.difficulty === 'easy' ? 'Kolay' :
               card.difficulty === 'medium' ? 'Orta' : 'Zor'}
            </Badge>
          </div>
        </div>
      </Card>
    )
  }
  
  return (
    <Card className="p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Kart #{card.id || 'Yeni'}
        </h3>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              console.log('Duplicate button clicked')
              onDuplicate()
            }}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <Copy size={14} className="mr-1" />
            Kopyala
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              console.log(`=== DELETE BUTTON CLICKED for card ${card.id || 'new'} ===`)
              console.log('Current showDeleteConfirm state:', showDeleteConfirm)
              console.log('Setting showDeleteConfirm to true...')
              setShowDeleteConfirm(true)
              console.log('setShowDeleteConfirm(true) called')
            }}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 size={14} className="mr-1" />
            Sil
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Question Side */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Soru *
          </label>
          <TextArea
            value={card.question}
            onChange={(e) => onUpdate({ ...card, question: e.target.value })}
            placeholder="Soruyu buraya yazın..."
            rows={4}
            className="mb-4"
          />
          
          <label className="block text-sm font-medium text-gray-700 mb-2">
            İpucu
          </label>
          <Input
            value={card.hint || ''}
            onChange={(e) => onUpdate({ ...card, hint: e.target.value })}
            placeholder="Opsiyonel ipucu..."
            className="mb-4"
          />
          
          {/* Image Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Görsel
            </label>
            
            {card.imageUrl ? (
              <div className="relative">
                <img 
                  src={card.imageUrl} 
                  alt="Kart görseli" 
                  className="w-full max-h-32 object-cover rounded border"
                />
                <button
                  onClick={() => onUpdate({ ...card, imageUrl: '' })}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-gray-400"
                onClick={() => setShowImageUpload(true)}
              >
                <Upload className="mx-auto text-gray-400 mb-2" size={24} />
                <p className="text-sm text-gray-600">Görsel yükle</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Answer Side */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cevap *
          </label>
          <TextArea
            value={card.answer}
            onChange={(e) => onUpdate({ ...card, answer: e.target.value })}
            placeholder="Cevabı buraya yazın..."
            rows={4}
            className="mb-4"
          />
          
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Açıklama
          </label>
          <TextArea
            value={card.explanation || ''}
            onChange={(e) => onUpdate({ ...card, explanation: e.target.value })}
            placeholder="Detaylı açıklama..."
            rows={3}
            className="mb-4"
          />
          
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zorluk Seviyesi
          </label>
          <select
            value={card.difficulty}
            onChange={(e) => onUpdate({ ...card, difficulty: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          >
            {DIFFICULTY_LEVELS.map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Tags */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Etiketler
        </label>
        
        <div className="flex flex-wrap gap-2 mb-2">
          {card.tags.map((tag, index) => (
            <Badge key={index} className="bg-blue-100 text-blue-800 flex items-center gap-1">
              {tag}
              <button
                onClick={() => handleTagRemove(tag)}
                className="ml-1 hover:text-blue-600"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
        
        <Input
          placeholder="Etiket ekle ve Enter'a bas..."
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleTagAdd(e.currentTarget.value)
              e.currentTarget.value = ''
            }
          }}
        />
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Kartı Sil"
      >
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Bu kartı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </p>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              İptal
            </Button>
            <Button
              onClick={() => {
                console.log('=== MODAL DELETE BUTTON CLICKED ===')
                console.log('About to call onDelete function')
                onDelete()
                console.log('onDelete function called, closing modal')
                setShowDeleteConfirm(false)
                console.log('=== MODAL DELETE COMPLETED ===')
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  )
}

const CreateFlashcard: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { deckId } = useParams<{ deckId?: string }>()
  const { currentDeck, loading } = useSelector((state: RootState) => state.flashcard)
  
  const [deckData, setDeckData] = useState<DeckFormData>({
    title: '',
    description: '',
    subject: '',
    topic: '',
    difficulty: 'medium',
    isPublic: false,
    isFavorite: false,
    tags: []
  })
  
  const [cards, setCards] = useState<CardFormData[]>([])
  const [previewMode, setPreviewMode] = useState(false)
  const [saving, setSaving] = useState(false)
  
  const subjects = LGS_SUBJECTS
  const availableTopics = deckData.subject ? getTopicsBySubject(deckData.subject) : []
  
  const isEditing = Boolean(deckId)
  
  useEffect(() => {
    if (deckId) {
      dispatch(fetchDeckById(deckId) as any)
    }
  }, [deckId, dispatch])
  
  useEffect(() => {
    if (currentDeck && isEditing && currentDeck.cards) {
      console.log('=== LOADING DECK DATA ===', currentDeck.id)
      console.log('Deck cards length:', currentDeck.cards.length)
      
      setDeckData({
        title: currentDeck.title,
        description: currentDeck.description || '',
        subject: currentDeck.subject,
        topic: currentDeck.topic || '',
        difficulty: currentDeck.difficulty,
        isPublic: currentDeck.isPublic,
        isFavorite: currentDeck.isFavorite || false,
        tags: currentDeck.tags || []
      })
      
      // Load existing cards
      const loadedCards = currentDeck.cards.map(card => ({
        id: card.id,
        question: card.question,
        answer: card.answer,
        hint: card.hint,
        explanation: card.explanation,
        imageUrl: card.imageUrl,
        tags: card.tags || [],
        difficulty: card.difficulty
      }))
      
      console.log('Setting cards:', loadedCards.length)
      setCards(loadedCards)
    }
  }, [currentDeck, isEditing])
  
  const addNewCard = () => {
    const newCard: CardFormData = {
      id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${Math.random().toString(36).substr(2, 9)}-${performance.now()}`,
      question: '',
      answer: '',
      hint: '',
      explanation: '',
      imageUrl: '',
      tags: [],
      difficulty: 'medium'
    }
    setCards([...cards, newCard])
  }
  
  const updateCard = (index: number, updatedCard: CardFormData) => {
    const newCards = [...cards]
    newCards[index] = updatedCard
    setCards(newCards)
  }
  
  const deleteCard = async (index: number) => {
    console.log('=== DELETE CARD FUNCTION CALLED ===')
    console.log('Deleting card at index:', index)
    console.log('Current cards array:', cards)
    console.log('Cards before delete:', cards.length)
    
    const targetCard = cards[index]
    const isPersisted = !!(targetCard?.id && !targetCard.id.startsWith('temp-'))
    const prevCards = cards
    
    // Optimistic update: remove locally first
    setCards(prevCards => {
      console.log('Previous cards in setter:', prevCards)
      const newCards = prevCards.filter((_, i) => {
        console.log(`Checking index ${i} against target ${index}: ${i !== index ? 'KEEP' : 'REMOVE'}`)
        return i !== index
      })
      
      console.log('Cards after filter:', newCards.length)
      console.log('New cards array:', newCards)
      console.log('=== LOCAL DELETE COMPLETED ===')
      return newCards
    })

    try {
      if (isEditing && deckId && isPersisted) {
        console.log('Dispatching deleteCardThunk for persisted card:', targetCard.id)
        await (dispatch as any)(deleteCardThunk({ deckId, cardId: targetCard.id! }) as any).then(unwrapResult)
        console.log('deleteCardThunk completed successfully for card:', targetCard.id)
      } else {
        console.log('Card is not persisted or not in edit mode; no backend delete required')
      }
    } catch (error) {
      console.error('Error deleting card from backend:', error)
      // Rollback local state
      setCards(() => {
        console.log('Rolling back local cards state due to delete error')
        return prevCards
      })
      alert('Kart silinirken bir hata oluştu. Lütfen tekrar deneyin.')
    }
  }
  
  const duplicateCard = (index: number) => {
    console.log('Duplicating card at index:', index)
    const cardToDuplicate = cards[index]
    const duplicatedCard: CardFormData = {
      ...cardToDuplicate,
      id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${Math.random().toString(36).substr(2, 9)}-${performance.now()}`,
      question: cardToDuplicate.question + ' (Kopya)'
    }
    console.log('Cards before duplicate:', cards.length, 'Cards after duplicate:', cards.length + 1)
    setCards([...cards, duplicatedCard])
  }
  
  const handleSave = async () => {
    console.log('=== SAVE STARTED ===')
    console.log('Cards to save:', cards.length)
    console.log('Cards IDs:', cards.map(c => c.id))
    console.log('Is editing:', isEditing)
    console.log('Deck ID:', deckId)
    console.log('Current deck cards count:', currentDeck?.cards?.length || 0)
    
    if (!deckData.title.trim() || !deckData.subject || cards.length === 0) {
      alert('Lütfen deste başlığı, ders seçimi yapın ve en az bir kart ekleyin.')
      return
    }
    
    if (cards.some(card => !card.question.trim() || !card.answer.trim())) {
      alert('Lütfen tüm kartların soru ve cevap alanlarını doldurun.')
      return
    }
    
    setSaving(true)
    
    try {
      const deckPayload = {
        ...deckData
      }
      
      let savedDeckId: string
      
      if (isEditing && deckId) {
        await dispatch(updateDeck({
          deckId,
          updates: deckPayload
        })).then(unwrapResult)
        savedDeckId = deckId
      } else {
        const result = await dispatch(createDeck(deckPayload)).then(unwrapResult)
        // Redux Toolkit async thunk returns the payload directly
        savedDeckId = result.id
        if (!savedDeckId) {
          throw new Error('Failed to get deck ID from create operation')
        }
      }
      
      // Save cards - optimize to prevent duplication
      console.log('=== SAVING CARDS ===', cards.length)
      
      // Separate existing and new cards
      const existingCards = cards.filter(card => card.id && !card.id.startsWith('temp-'))
      const newCards = cards.filter(card => !card.id || card.id.startsWith('temp-'))
      
      console.log('Existing cards to update:', existingCards.length)
      console.log('New cards to create:', newCards.length)
      
      // Update existing cards
      for (const card of existingCards) {
        try {
          const cardPayload = {
            ...card,
            deckId: savedDeckId
          }
          console.log('Updating card:', card.id)
          await dispatch(updateCardThunk({
            cardId: card.id!,
            updates: cardPayload
          }) as any).then(unwrapResult)
        } catch (error) {
          console.error('Error updating card:', card.id, error)
          throw new Error(`Card update failed: ${error}`)
        }
      }
      
      // Create new cards
      for (const card of newCards) {
        try {
          const cardPayload = {
            ...card,
            deckId: savedDeckId
          }
          delete (cardPayload as any).id // Remove temp ID
          console.log('Creating new card')
          await dispatch(createCardThunk(cardPayload as any) as any).then(unwrapResult)
        } catch (error) {
          console.error('Error creating card:', error)
          throw new Error(`Card creation failed: ${error}`)
        }
      }
      
      console.log('=== CARDS SAVED ===')
      
      // Kaydetme işleminden sonra deck'i yeniden yüklemeyi kaldır
      // çünkü bu kartların çoğalmasına neden olabilir
      
      navigate('/admin/flashcards')
    } catch (error) {
      console.error('Error saving deck:', error)
      alert('Deste kaydedilirken bir hata oluştu.')
    } finally {
      setSaving(false)
    }
  }
  
  const handleDeckTagAdd = (tag: string) => {
    if (tag.trim() && !deckData.tags.includes(tag.trim())) {
      setDeckData({
        ...deckData,
        tags: [...deckData.tags, tag.trim()]
      })
    }
  }
  
  const handleDeckTagRemove = (tagToRemove: string) => {
    setDeckData({
      ...deckData,
      tags: deckData.tags.filter(tag => tag !== tagToRemove)
    })
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/flashcards')}
            >
              <ArrowLeft size={16} className="mr-2" />
              Geri
            </Button>
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? 'Deste Düzenle' : 'Yeni Deste Oluştur'}
              </h1>
              <p className="text-gray-600">
                {isEditing ? 'Mevcut desteyi düzenle' : 'Yeni bir flashcard destesi oluştur'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? <EyeOff size={16} /> : <Eye size={16} />}
              <span className="ml-2">
                {previewMode ? 'Düzenleme' : 'Önizleme'}
              </span>
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
        
        {/* Deck Information */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Deste Bilgileri
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deste Başlığı *
              </label>
              <Input
                value={deckData.title}
                onChange={(e) => setDeckData({ ...deckData, title: e.target.value })}
                placeholder="Deste başlığını girin..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ders *
              </label>
              <select
                value={deckData.subject}
                onChange={(e) => setDeckData({ ...deckData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Ders seçin...</option>
                {subjects.map(subject => (
                  <option key={subject.value} value={subject.value}>
                    {subject.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Konu *
              </label>
              <select
                value={deckData.topic}
                onChange={(e) => setDeckData({ ...deckData, topic: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!deckData.subject}
              >
                <option value="">Konu seçin...</option>
                {availableTopics.map(topic => (
                  <option key={topic.value} value={topic.value}>
                    {topic.label}
                  </option>
                ))}
              </select>
              {!deckData.subject && (
                <p className="text-sm text-gray-500 mt-1">Önce ders seçin</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zorluk Seviyesi
              </label>
              <select
                value={deckData.difficulty}
                onChange={(e) => setDeckData({ ...deckData, difficulty: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DIFFICULTY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Açıklama
            </label>
            <TextArea
              value={deckData.description}
              onChange={(e) => setDeckData({ ...deckData, description: e.target.value })}
              placeholder="Deste hakkında kısa açıklama..."
              rows={3}
            />
          </div>
          
          <div className="mt-4 flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={deckData.isPublic}
                onChange={(e) => setDeckData({ ...deckData, isPublic: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Herkese açık</span>
            </label>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={deckData.isFavorite}
                onChange={(e) => setDeckData({ ...deckData, isFavorite: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Favorilere ekle</span>
            </label>
          </div>
          
          {/* Deck Tags */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Etiketler
            </label>
            
            <div className="flex flex-wrap gap-2 mb-2">
              {deckData.tags.map((tag, index) => (
                <Badge key={index} className="bg-blue-100 text-blue-800 flex items-center gap-1">
                  {tag}
                  <button
                    onClick={() => handleDeckTagRemove(tag)}
                    className="ml-1 hover:text-blue-600"
                  >
                    <X size={12} />
                  </button>
                </Badge>
              ))}
            </div>
            
            <Input
              placeholder="Etiket ekle ve Enter'a bas..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleDeckTagAdd(e.currentTarget.value)
                  e.currentTarget.value = ''
                }
              }}
            />
          </div>
        </Card>
        
        {/* Cards Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Kartlar ({cards.length})
          </h2>
          
          <Button
            onClick={addNewCard}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus size={16} className="mr-2" />
            Yeni Kart
          </Button>
        </div>
        
        {cards.length === 0 ? (
          <Card className="p-12 text-center">
            <Type className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Henüz kart eklenmemiş
            </h3>
            <p className="text-gray-600 mb-6">
              İlk kartını ekleyerek başla
            </p>
            <Button
              onClick={addNewCard}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus size={16} className="mr-2" />
              İlk Kartı Ekle
            </Button>
          </Card>
        ) : (
          <div>
            {cards.map((card, index) => {
              // Generate stable unique key - use card.id if available (including temp ids), fallback to index
              const uniqueKey = card.id 
                ? `card-${card.id}` 
                : `temp-idx-${index}`
              
              return (
                <CardEditor
                  key={uniqueKey}
                  card={card}
                  onUpdate={(updatedCard) => {
                    console.log(`Update card at index ${index}:`, updatedCard)
                    updateCard(index, updatedCard)
                  }}
                  onDelete={() => {
                    console.log(`Delete triggered for card at index ${index}, card id: ${card.id}`)
                    deleteCard(index)
                  }}
                  onDuplicate={() => {
                    console.log(`Duplicate triggered for card at index ${index}, card id: ${card.id}`)
                    duplicateCard(index)
                  }}
                  isPreview={previewMode}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default CreateFlashcard